import { produce, Draft } from "immer";
import Axios from "../apis/axios";

interface TopologySlice {
  userNodeId?: string;
  legalEntityNodeId?: string;

  subscribedSites?: Record<string, any>;
  subscribedAreas?: Record<string, any>;
  subscribedEquipmentpools?: Record<string, any>;

  loadTopology?: (lang?: string) => Promise<any>;
  getSingleEquipment?: (equipmentNodeId?: string) => any;
  getSiteOfEquipment?: (equipmentNodeId?: string) => any;
  getAreaOfEquipment?: (equipmentNodeId?: string) => any;
  getEquipmentpoolOfEquipment?: (equipmentNodeId?: string) => any;
  createSite?: (payload?: any) => void;
  updateTopologyInStateUpdateEquipment?: (equipment?: any) => void;
  getNetConnectConfiguration?: () => Promise<any>;
}

export const createTopologySlice = (
  set: (data: TopologySlice) => void,
  get: () => any
) => ({
  userNodeId: "",
  legalEntityNodeId: "",
  subscribedSites: {},
  subscribedAreas: {},
  subscribedEquipmentpools: {},

  emptySites: () => {
    set({
      subscribedSites: {},
    });
  },

  loadTopology: async (lang?: string) => {
    try {
      if (lang == undefined) {
        lang = "de";
      }
      const response = await Axios.get(
        "api/v1/sitesareasequipments2?lang=" + lang,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response && response.status && response?.status == 200) {
        processTopologyData(set, response.data);
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      console.log(error);
    }
  },

  getSingleEquipment: (equipmentNodeId: string) => {
    for (const key in get().subscribedSites) {
      const site = get().subscribedSites[key];
      if (site.hasarea) {
        for (const areaItem of site.hasarea) {
          if (areaItem?.area?.hasequipment) {
            for (const equipmentItem of areaItem?.area?.hasequipment) {
              if (equipmentItem.equipment.nodeid == equipmentNodeId) {
                return equipmentItem;
              }
            }
          }
        }
      }
    }
    return null;
  },

  getSiteOfEquipment: (equipmentNodeId: string) => {
    for (const key in get().subscribedSites) {
      const site = get().subscribedSites[key];
      if (site.hasarea) {
        for (const areaItem of site.hasarea) {
          if (areaItem?.area?.hasequipment) {
            for (const equipmentItem of areaItem?.area?.hasequipment) {
              if (equipmentItem.equipment.nodeid == equipmentNodeId) {
                return site;
              }
            }
          }
        }
      }
    }
    return null;
  },

  getAreaOfEquipment: (equipmentNodeId: string) => {
    for (const key in get().subscribedAreas) {
      const area = get().subscribedAreas[key];
      if (area.hasequipment) {
        for (const equipmentItem of area?.hasequipment) {
          if (equipmentItem.equipment.nodeid == equipmentNodeId) {
            return area;
          }
        }
      }
    }
    return null;
  },

  getEquipmentpoolOfEquipment: (equipmentNodeId: string) => {
    for (const key in get().subscribedEquipmentpools) {
      const equipmentpool = get().subscribedEquipmentpools[key];
      for (const equipmentItem of equipmentpool.hasequipment) {
        if (equipmentItem.equipment.nodeid == equipmentNodeId) {
          return equipmentpool;
        }
      }
    }
    return null;
  },

  getEquipmentsOfArea: (areaNodeId: string) => {
    const equipments = [];
    const area = get().subscribedAreas[areaNodeId];

    if (area !== undefined && area?.hasequipment) {
      for (const equipmentItem of area?.hasequipment) {
        equipments.push(equipmentItem.equipment);
      }
      return equipments;
    }

    return [];
  },

  getEquipmentsOfSite: (siteNodeId: string) => {
    const equipments = [];
    const site = get().subscribedSites[siteNodeId];

    if (site !== undefined) {
      if (site.hasarea) {
        for (const areaItem of site.hasarea) {
          if (areaItem?.area?.hasequipment) {
            for (const equipmentItem of areaItem?.area?.hasequipment) {
              equipments.push(equipmentItem.equipment);
            }
          }
        }
      }
      return equipments;
    }

    return [];
  },

  createSite: (payload: any) => {
    if (payload.legalentitynodeid === undefined) {
      payload.legalentitynodeid = get().legalEntityNodeId;
    }

    return get().enqueueCommand("processCreateSiteCommand", payload);
  },

  getNetConnectConfiguration: async (
    legalentitynodeid: string,
    lang: string
  ) => {
    try {
      if (legalentitynodeid === undefined) {
        legalentitynodeid = get().legalEntityNodeId;
      }

      if (lang == undefined) {
        lang = "de";
      }

      const response = await Axios.get(
        "api/v1/netconnectconfig?legalentitynodeid=" +
          legalentitynodeid +
          "&lang=" +
          lang,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      if (response && response.status && response?.status === 200) {
        return response.data;
      } else {
        throw new Error();
      }
    } catch (error) {
      throw new Error(String(error));
    }
  },
});

const processTopologyData = (set: any, data: any) => {
  const subscribedEquipmentpools: any = {};
  const subscribedSites: any = {};
  const subscribedAreas: any = {};
  const subscribedEquipments: any = {};

  if (data[0]?.data[0]?.user?.issubscriberof?.length > 0) {
    // Sort the array by the 'order' property numerically
    data[0]?.data[0]?.user?.issubscriberof.sort((site1: any, site2: any) => {
      const order1 = parseInt(site1.site.order);
      const order2 = parseInt(site2.site.order);

      return order1 - order2;
    });

    for (const subscribedItem of data[0]?.data[0]?.user?.issubscriberof) {
      if (subscribedItem.equipmentpool != undefined) {
        subscribedEquipmentpools[subscribedItem.equipmentpool.nodeid] =
          subscribedItem.equipmentpool;
        for (const equipmentItem of subscribedItem.equipmentpool.hasequipment) {
          subscribedEquipments[equipmentItem.equipment.nodeid] =
            equipmentItem.equipment;
        }
      }

      if (subscribedItem.site != undefined) {
        subscribedSites[subscribedItem.site.nodeid] = subscribedItem.site;
        if (subscribedItem.site.hasarea) {
          for (const areaItem of subscribedItem.site.hasarea) {
            subscribedAreas[areaItem.area.nodeid] = areaItem.area;
            if (areaItem?.area?.hasequipment) {
              for (const equipmentItem of areaItem?.area?.hasequipment) {
                subscribedEquipments[equipmentItem.equipment.nodeid] =
                  equipmentItem.equipment;
              }
            }
          }
        }
      }

      if (subscribedItem.area != undefined) {
        const siteItem = subscribedItem.area.isareaof[0];
        if (subscribedSites[siteItem.site.nodeid] === undefined) {
          siteItem.site.hasarea = [];
          subscribedSites[siteItem.site.nodeid] = siteItem.site;
        }
        if (
          subscribedSites[siteItem.site.nodeid].hasarea.findIndex(
            (item: any) => item.area.nodeid === subscribedItem.area.nodeid
          ) === -1
        ) {
          subscribedSites[siteItem.site.nodeid].hasarea.push(subscribedItem);
        }
        subscribedAreas[subscribedItem.area.nodeid] = subscribedItem.area;
        for (const equipmentItem of subscribedItem.area?.hasequipment) {
          subscribedEquipments[equipmentItem.equipment.nodeid] =
            equipmentItem.equipment;
        }
      }
    }

    for (const subscribedSitenodeId in subscribedSites) {
      subscribedSites[subscribedSitenodeId].hasarea &&
        subscribedSites[subscribedSitenodeId].hasarea.sort(function (
          a: { area: { order: number } },
          b: { area: { order: number } }
        ) {
          if (a.area.order < b.area.order) {
            return -1;
          }
          if (a.area.order > b.area.order) {
            return 1;
          }
          return 0;
        });
    }
  }

  set({
    subscribedSites: subscribedSites,
    subscribedAreas: subscribedAreas,
    subscribedEquipmentpools: subscribedEquipmentpools,
    _subscribedEquipments: subscribedEquipments,
    subscribedEquipmentsCalender: subscribedEquipments,
  });
};
